
import Vue from 'vue'
import { productMeta } from './product-meta'

const APPLE_HUNTER_CLICK_LOG_ENDPOINT = 'https://api.ari.company/api/form/game-click'

interface Highlight {
  title: string
  text: string
}

interface Screenshot {
  src: string
  alt: string
  caption: string
}

interface AssetPack extends Screenshot {
  title: string
}

export default Vue.extend({
  name: 'AppleHunterPage',
  metaInfo () { return productMeta('Apple Hunter for Android | ARI.company', 'Dodge enemies, collect apples and chase leaderboard results in Apple Hunter. Find gameplay, privacy and data-deletion information.', '/android/apple-hunter/assets/apple-hunter-logo-games.webp') },

  data () {
    return {
      reviewDrafts: process.env.VUE_APP_INCLUDE_REVIEW_DRAFTS === "true",
      googlePlayUrl: 'https://play.google.com/store/apps/details?id=com.ari.applehunter',
      highlights: [
        {
          title: 'Core Loop',
          text: 'Collect apples, avoid enemies, and keep control as difficulty accelerates over time.'
        },
        {
          title: 'Boosters & Intensity',
          text: 'Timed boosts and challenge spikes create dynamic pacing and better run-to-run variation.'
        },
        {
          title: 'Offline Ready',
          text: 'Runs smoothly offline with a lightweight session model built for mobile play windows.'
        }
      ] as Highlight[],
      screenshots: [
        {
          src: '/android/apple-hunter/screenshots/screenshot-0.webp',
          alt: 'Apple Hunter gameplay screenshot 1',
          caption: 'Early run setup with open apple routes.'
        },
        {
          src: '/android/apple-hunter/screenshots/screenshot-1.webp',
          alt: 'Apple Hunter gameplay screenshot 2',
          caption: 'Higher pressure phase with denser enemy movement.'
        },
        {
          src: '/android/apple-hunter/screenshots/screenshot-2.webp',
          alt: 'Apple Hunter gameplay screenshot 3',
          caption: 'Score-building moment with route optimisation.'
        },
        {
          src: '/android/apple-hunter/screenshots/screenshot-3.webp',
          alt: 'Apple Hunter gameplay screenshot 4',
          caption: 'Booster timing for survivability and momentum.'
        },
        {
          src: '/android/apple-hunter/screenshots/screenshot-4.webp',
          alt: 'Apple Hunter gameplay screenshot 5',
          caption: 'Challenge wave where reflex control is critical.'
        },
        {
          src: '/android/apple-hunter/screenshots/screenshot-5.webp',
          alt: 'Apple Hunter gameplay screenshot 6',
          caption: 'Late-session phase with aggressive pacing.'
        }
      ] as Screenshot[],
      universeShowcase: [
        {
          src: '/android/apple-hunter/library/splash_screen.webp',
          alt: 'Apple Hunter splash poster',
          title: 'Main Splash Poster',
          caption: 'Primary visual composition for launch and store positioning.'
        },
        {
          src: '/android/apple-hunter/library/logo.webp',
          alt: 'Apple Hunter logo',
          title: 'Title Lockup',
          caption: 'Core game branding used across promotional placements.'
        },
        {
          src: '/android/apple-hunter/library/icon-1.webp',
          alt: 'Apple Hunter secondary app icon',
          title: 'Store Icon Variant',
          caption: 'Alternative icon treatment for creative testing and campaigns.'
        }
      ] as AssetPack[],
      heroRoster: [
        {
          src: '/android/apple-hunter/library/hero_girl.webp',
          alt: 'Hero girl',
          title: 'Explorer Girl',
          caption: 'Balanced starter hero.'
        },
        {
          src: '/android/apple-hunter/library/hero_apple.webp',
          alt: 'Hero apple',
          title: 'Apple Runner',
          caption: 'Score-focused apple archetype.'
        },
        {
          src: '/android/apple-hunter/library/hero_robot.webp',
          alt: 'Hero robot',
          title: 'Robo Scout',
          caption: 'Precision mobility profile.'
        },
        {
          src: '/android/apple-hunter/library/hero_raccoon.webp',
          alt: 'Hero raccoon',
          title: 'Raccoon Hero',
          caption: 'Agile route optimiser.'
        },
        {
          src: '/android/apple-hunter/library/hero_panda.webp',
          alt: 'Hero panda',
          title: 'Panda Guardian',
          caption: 'Defensive tempo style.'
        },
        {
          src: '/android/apple-hunter/library/hero_mushroom.webp',
          alt: 'Hero mushroom',
          title: 'Mushroom Mage',
          caption: 'Utility-focused support hero.'
        },
        {
          src: '/android/apple-hunter/library/hero_dino.webp',
          alt: 'Hero dino',
          title: 'Dino Ranger',
          caption: 'Power burst specialist.'
        },
        {
          src: '/android/apple-hunter/library/hero_dragon.webp',
          alt: 'Hero dragon',
          title: 'Dragon Rookie',
          caption: 'High-risk mobility profile.'
        },
        {
          src: '/android/apple-hunter/library/hero_dog.webp',
          alt: 'Hero dog',
          title: 'Dog Companion',
          caption: 'Steady pace character.'
        },
        {
          src: '/android/apple-hunter/library/hero_shark.webp',
          alt: 'Hero shark',
          title: 'Shark Blitzer',
          caption: 'Fast acceleration class.'
        },
        {
          src: '/android/apple-hunter/library/hero_lion.webp',
          alt: 'Hero lion',
          title: 'Lion Vanguard',
          caption: 'Heavy pressure leader.'
        },
        {
          src: '/android/apple-hunter/library/cat.webp',
          alt: 'Cat companion',
          title: 'Cat Ally',
          caption: 'Companion mascot profile.'
        },
        {
          src: '/android/apple-hunter/library/dog.webp',
          alt: 'Dog companion',
          title: 'Dog Ally',
          caption: 'Friendly support companion.'
        },
        {
          src: '/android/apple-hunter/library/lion.webp',
          alt: 'Lion companion',
          title: 'Lion Ally',
          caption: 'Courage-themed mascot.'
        }
      ] as AssetPack[],
      lootAndBoosters: [
        {
          src: '/android/apple-hunter/library/apple.webp',
          alt: 'Apple pickup',
          title: 'Apple Pickup',
          caption: 'Core scoring resource.'
        },
        {
          src: '/android/apple-hunter/library/golden_apple.webp',
          alt: 'Golden apple pickup',
          title: 'Golden Apple',
          caption: 'High-value score trigger.'
        },
        {
          src: '/android/apple-hunter/library/triple_apple.webp',
          alt: 'Triple apple pack',
          title: 'Triple Apple',
          caption: 'Cluster pickup with combo potential.'
        },
        {
          src: '/android/apple-hunter/library/five_apple.webp',
          alt: 'Five apple pack',
          title: 'Five Apple Pack',
          caption: 'Dense resource reward.'
        },
        {
          src: '/android/apple-hunter/library/double_booster.webp',
          alt: 'Double booster',
          title: 'Double Booster',
          caption: 'Temporary output multiplier.'
        },
        {
          src: '/android/apple-hunter/library/meat.webp',
          alt: 'Meat pickup',
          title: 'Meat Boost',
          caption: 'Survival-oriented pickup.'
        },
        {
          src: '/android/apple-hunter/library/meat-2.webp',
          alt: 'Meat pickup variant',
          title: 'Meat Boost Variant',
          caption: 'Alternative nutrition bonus.'
        },
        {
          src: '/android/apple-hunter/library/cat_shield.webp',
          alt: 'Cat shield',
          title: 'Cat Shield',
          caption: 'Protective defence utility.'
        }
      ] as AssetPack[],
      enemyAndBossBoard: [
        {
          src: '/android/apple-hunter/library/spider_enemy.webp',
          alt: 'Spider enemy',
          title: 'Spider Enemy',
          caption: 'Early disruption unit.'
        },
        {
          src: '/android/apple-hunter/library/wolf.webp',
          alt: 'Wolf enemy',
          title: 'Wolf Enemy',
          caption: 'Aggressive chaser archetype.'
        },
        {
          src: '/android/apple-hunter/library/lion_enemy.webp',
          alt: 'Lion enemy',
          title: 'Lion Enemy',
          caption: 'Heavy impact encounter.'
        },
        {
          src: '/android/apple-hunter/library/boss/boss_apple.webp',
          alt: 'Apple boss',
          title: 'Corrupted Apple Boss',
          caption: 'Main boss encounter phase.'
        },
        {
          src: '/android/apple-hunter/library/boss/boss_bombs_apple.webp',
          alt: 'Apple bomb boss',
          title: 'Explosive Apple Boss',
          caption: 'Bomb-pattern pressure mechanic.'
        },
        {
          src: '/android/apple-hunter/library/boss/boss_bombs_tomato.webp',
          alt: 'Tomato bomb boss',
          title: 'Tomato Bomb Variant',
          caption: 'Alternative explosive boss pattern.'
        }
      ] as AssetPack[],
      lostStates: [
        {
          src: '/android/apple-hunter/library/lost/hero_apple.webp',
          alt: 'Lost state apple hero',
          title: 'Apple Hero Lost State',
          caption: 'Defeat-state expression.'
        },
        {
          src: '/android/apple-hunter/library/lost/hero_cat.webp',
          alt: 'Lost state cat hero',
          title: 'Cat Hero Lost State',
          caption: 'Defeat-state expression.'
        },
        {
          src: '/android/apple-hunter/library/lost/hero_dino.webp',
          alt: 'Lost state dino hero',
          title: 'Dino Hero Lost State',
          caption: 'Defeat-state expression.'
        },
        {
          src: '/android/apple-hunter/library/lost/hero_dog.webp',
          alt: 'Lost state dog hero',
          title: 'Dog Hero Lost State',
          caption: 'Defeat-state expression.'
        },
        {
          src: '/android/apple-hunter/library/lost/hero_dragon.webp',
          alt: 'Lost state dragon hero',
          title: 'Dragon Hero Lost State',
          caption: 'Defeat-state expression.'
        },
        {
          src: '/android/apple-hunter/library/lost/hero_girl.webp',
          alt: 'Lost state girl hero',
          title: 'Explorer Girl Lost State',
          caption: 'Defeat-state expression.'
        },
        {
          src: '/android/apple-hunter/library/lost/hero_lion.webp',
          alt: 'Lost state lion hero',
          title: 'Lion Hero Lost State',
          caption: 'Defeat-state expression.'
        },
        {
          src: '/android/apple-hunter/library/lost/hero_mushroom.webp',
          alt: 'Lost state mushroom hero',
          title: 'Mushroom Hero Lost State',
          caption: 'Defeat-state expression.'
        },
        {
          src: '/android/apple-hunter/library/lost/hero_panda.webp',
          alt: 'Lost state panda hero',
          title: 'Panda Hero Lost State',
          caption: 'Defeat-state expression.'
        },
        {
          src: '/android/apple-hunter/library/lost/hero_raccoon.webp',
          alt: 'Lost state raccoon hero',
          title: 'Raccoon Hero Lost State',
          caption: 'Defeat-state expression.'
        },
        {
          src: '/android/apple-hunter/library/lost/hero_robot.webp',
          alt: 'Lost state robot hero',
          title: 'Robot Hero Lost State',
          caption: 'Defeat-state expression.'
        },
        {
          src: '/android/apple-hunter/library/lost/hero_shark.webp',
          alt: 'Lost state shark hero',
          title: 'Shark Hero Lost State',
          caption: 'Defeat-state expression.'
        }
      ] as AssetPack[],
      activeScreenshot: null as Screenshot | null
    }
  },

  mounted () {
    window.addEventListener('keydown', this.handleLightboxEscape)
  },

  beforeDestroy () {
    window.removeEventListener('keydown', this.handleLightboxEscape)
  },

  methods: {
    trackStoreClick (buttonLabel: string, source: string) {
      const payload = {
        button: buttonLabel,
        source,
        page: window.location.pathname,
        destination: this.googlePlayUrl,
        clicked_at: new Date().toISOString()
      }

      try {
        if (navigator.sendBeacon) {
          const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' })
          if (navigator.sendBeacon(APPLE_HUNTER_CLICK_LOG_ENDPOINT, blob)) {
            return
          }
        }
      } catch (e) {
        // Fallback to fetch below.
      }

      fetch(APPLE_HUNTER_CLICK_LOG_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
        keepalive: true
      }).catch(() => {
        // Do not block store redirect if logging fails.
      })
    },

    openScreenshotModal (item: Screenshot) {
      this.activeScreenshot = item
    },

    closeScreenshotModal () {
      this.activeScreenshot = null
    },

    handleLightboxEscape (event: KeyboardEvent) {
      if (event.key === 'Escape' && this.activeScreenshot) {
        this.closeScreenshotModal()
      }
    }
  }
})
